import { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import ThemeToggle from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import { Carrot, Copy, Search, MessagesSquare, Mail, Layers, ArrowRight, Check, Sprout } from "lucide-react";

export default function Landing() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const d = new Date();
  const CODE = `^Carrot${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}${String(d.getFullYear()).slice(-2)}`;

  useEffect(() => {
    if (!loading && user) navigate("/dashboard", { replace: true });
  }, [user, loading, navigate]);

  const signIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const origin = window.location.origin;
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/google/login?origin=${encodeURIComponent(origin)}`;
  };

  const places = [
    { icon: MessagesSquare, tint: "text-indigo-600 dark:text-indigo-400", bg: "bg-indigo-500/10", label: "Ops thread", sub: "Team messaging app" },
    { icon: Mail, tint: "text-blue-600 dark:text-blue-400", bg: "bg-blue-500/10", label: "Re: New client submission", sub: "Email" },
    { icon: Layers, tint: "text-emerald-600 dark:text-emerald-400", bg: "bg-emerald-500/10", label: "Project discussion", sub: "Project management tool" },
  ];

  const steps = [
    { icon: Sprout, title: "Plant it", desc: "Describe the task. Carrot turns it into one clean, unique, searchable Root and adds it to your Field." },
    { icon: Copy, title: "Spread it", desc: "Drop the Root into the message thread, email, project, or document, all the places the task happens." },
    { icon: Carrot, title: "Tend it", desc: "Your Field is where you monitor and organize everything that still needs to get done. Prioritize what matters, or snooze tasks until you're ready to come back to them." },
    { icon: Search, title: "Pull it", desc: "When you're ready to pick it back up, search the Root in any of your apps to jump straight back to the work and finish it off." },
  ];

  return (
    <div className="min-h-screen bg-background grain relative overflow-hidden">
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-primary/10 blur-3xl" />
      <div className="absolute top-1/3 -left-40 w-96 h-96 rounded-full bg-emerald-500/10 blur-3xl" />

      <header className="relative z-10 max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center">
            <Carrot className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-extrabold tracking-tight">Carrot</span>
        </div>
        <div className="flex items-center gap-2">
          <ThemeToggle />
          <Button onClick={signIn} data-testid="header-signin-btn" variant="ghost" className="font-semibold">Sign in</Button>
        </div>
      </header>

      <main className="relative z-10 max-w-6xl mx-auto px-6 pt-8 pb-14">
        {/* Hero */}
        <div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="min-w-0">
            <h1 className="text-2xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.2]">
              <span className="block lg:whitespace-nowrap">You have too many things to do.</span>
              <span className="block lg:whitespace-nowrap">You have no good way to find it.</span>
              <span className="block lg:whitespace-nowrap">
                You work across <span className="text-primary">multiple apps, platforms, and places.</span>
              </span>
            </h1>
            <p className="mt-6 text-lg sm:text-xl font-bold text-foreground max-w-3xl leading-snug">
              Carrot doesn't exist because we think you need a new app. Carrot exists because you already have too many.
            </p>
            <div className="mt-7">
              <Button
                onClick={signIn}
                data-testid="google-signin-button"
                size="lg"
                className="rounded-full bg-primary hover:bg-[#E85D04] text-white font-semibold gap-2 h-12 px-6 shadow-lg shadow-primary/20"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#fff" d="M12.24 10.4v3.83h5.44c-.24 1.4-1.66 4.1-5.44 4.1-3.27 0-5.94-2.7-5.94-6.04s2.67-6.04 5.94-6.04c1.86 0 3.11.79 3.83 1.48l2.6-2.5C17.03 3.4 14.86 2.4 12.24 2.4 6.98 2.4 2.7 6.68 2.7 12s4.28 9.6 9.54 9.6c5.51 0 9.16-3.87 9.16-9.33 0-.63-.07-1.1-.15-1.58l-9.01-.29z"/></svg>
                Continue with Google
              </Button>
            </div>
          </motion.div>

          {/* Concept visual: one code planted in 3 places */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="mt-10 rounded-3xl border border-border bg-card p-6 shadow-xl min-w-0 max-w-2xl overflow-hidden"
          >
            <div className="flex items-center justify-center gap-2 mb-5">
              <span className="text-sm text-muted-foreground">Plant</span>
              <span className="font-mono font-bold text-lg px-3 py-1.5 rounded-lg bg-primary/10 text-primary border border-primary/30">
                {CODE}
              </span>
              <span className="text-sm text-muted-foreground">in each place →</span>
            </div>
            <div className="space-y-2.5">
              {places.map((p, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: 12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + i * 0.1 }}
                  className="flex items-center gap-3 rounded-xl border border-border bg-secondary/40 px-3 py-2.5"
                >
                  <div className={`w-8 h-8 rounded-lg ${p.bg} flex items-center justify-center shrink-0`}>
                    <p.icon className={`w-4 h-4 ${p.tint}`} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate">{p.label}</div>
                    <div className="text-xs text-muted-foreground">{p.sub}</div>
                  </div>
                  <span className="font-mono text-xs font-bold text-primary shrink-0">{CODE}</span>
                </motion.div>
              ))}
            </div>
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="mt-4 flex items-center gap-2 rounded-xl border border-primary/30 bg-primary/5 px-3 py-2.5"
            >
              <Search className="w-4 h-4 text-primary shrink-0" />
              <span className="text-xs text-muted-foreground shrink-0">Search</span>
              <span className="font-mono text-sm font-bold flex-1">{CODE}</span>
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                <Check className="w-3.5 h-3.5" /> in any app, find all 3
              </span>
            </motion.div>
          </motion.div>
        </div>

        {/* How it works */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
          {steps.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              className="rounded-2xl border border-border bg-card p-5"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                  <s.icon className="w-5 h-5 text-primary" />
                </div>
                <span className="text-xs font-bold text-muted-foreground">STEP {i + 1}</span>
              </div>
              <h3 className="font-bold text-lg mb-1">{s.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>

        {/* Footer CTA */}
        <div className="mt-12 rounded-3xl border border-border bg-card p-8 text-center relative overflow-hidden">
          <div className="absolute -top-16 -right-10 w-64 h-64 rounded-full bg-primary/10 blur-3xl" />
          <div className="relative z-10">
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight">Stop hunting. Start pulling Roots.</h2>
            <p className="mt-3 text-muted-foreground max-w-lg mx-auto">
              The goal is simple: use Carrot to tag, organize, find, reschedule, and complete your tasks daily, so when you go home you know you're not leaving anything behind.
            </p>
            <Button
              onClick={signIn}
              data-testid="footer-signin-btn"
              size="lg"
              className="mt-6 rounded-full bg-primary hover:bg-[#E85D04] text-white font-semibold gap-2 h-12 px-6 shadow-lg shadow-primary/20"
            >
              Get started free <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Footer */}
        <footer className="mt-10 pt-6 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-muted-foreground">
          <span>© {new Date().getFullYear()} Carrot</span>
          <div className="flex items-center gap-5">
            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-foreground font-medium">Privacy Policy</Link>
            <Link to="/terms" data-testid="footer-terms-link" className="hover:text-foreground font-medium">Terms of Service</Link>
          </div>
        </footer>
      </main>
    </div>
  );
}
